export const screens = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
};

export const media = {
  sm: `@media (min-width: ${screens.sm})`,
  md: `@media (min-width: ${screens.md})`,
  lg: `@media (min-width: ${screens.lg})`,
  xl: `@media (min-width: ${screens.xl})`,
};

export const breakpoints = [screens.sm, screens.md, screens.lg, screens.xl];

export const colors = {
  gray: {
    100: '#f7fafc',
    200: '#edf2f7',
    300: '#e2e8f0',
    400: '#cbd5e0',
    500: '#a1aec1',
    600: '#718096',
    700: '#4a5569',
    800: '#2d3748',
    900: '#1a202c',
  },

  olive: {
    50: '#F3F6F3',
    100: '#DEE4DF',
    200: '#C3CEC4',
    300: '#889E8A',
    400: '#608663',
    500: '#3E704B',
    600: '#2A5E38',
    700: '#164522',
    800: '#10361B',
    900: '#233933',
  },

  dark: {
    100: '#4B514D',
    200: '#222720',
    300: '#1A1A1A',
    900: '#000000',
  },

  light: {
    100: '#FFFFFF',
    200: '#FFFDF7',
  },

  yellow: {
    100: '#FFF9DE',
    200: '#FFF0A4',
    300: '#FDE56F',
    400: '#FFDD33',
    500: '#FAD44E',
    600: '#EDC94A',
    700: '#E5BC2A',
    800: '#D0A81C',
    900: '#917513',
  },

  brown: {
    100: '#f5f2ed',
    200: '#e1ddd5',
    300: '#e2caa5',
    500: '#d1b398',
    900: '#a36731',
  },

  red: {
    100: '#FFE9E5',
    200: '#FFC4B9',
    300: '#F59A89',
    400: '#F36C53',
    500: '#E2430C',
    600: '#ED3615',
    900: '#CA2D10',
  },

  accent: {
    green: '#4CA817',
    gold: '#FFBA00',
  },
};

export const fonts = {
  base: `'Open Sans', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji',
    'Segoe UI Symbol', 'Noto Color Emoji'`,
  headings: `'Open Sans', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji',
    'Segoe UI Symbol', 'Noto Color Emoji'`,
  playfair: `'Playfair Display', serif`,
};

export const fontWeights = {
  light: 300,
  normal: 400,
  medium: 600,
  bold: 700,
  bolder: 800,
};

export const fontSizes = {
  xxs: '0.625rem', // 10px
  xs: '0.75rem', // 12px
  sm: '0.875rem', // 14px
  base: '1rem', // 16px
  lg: '1.125rem', // 18px
  xl: '1.25rem', // 20px
  '2xl': '1.5rem', // 24px
  '3xl': '1.875rem', // 30px
  '4xl': '2.25rem', // 36px
  '5xl': '3rem', // 48px
  '6xl': '4rem', // 64px
  '7xl': '4.5rem', // 72px
};

export const headingSizes = {
  xl: '60px',
  lg: '48px',
  md: '40px',
  sm: '32px',
  xs: '24px',
  ['2xs']: '20px',
};

export const lineHeights = {
  none: 1,
  tight: 1.25,
  snug: 1.375,
  normal: 1.5,
  relaxed: 1.625,
  loose: 2,
};

export const space = {
  0: '0rem',
  1: '0.25rem', // 4px
  2: '0.5rem', // 8px
  3: '0.75rem', // 12px
  4: '1rem', // 16px
  5: '1.25rem', // 20px
  6: '1.5rem', // 24px
  7: '1.75rem', // 28px
  8: '2rem', // 32px
  9: '2.25rem', // 36px
  10: '2.5rem', // 40px
  11: '2.75rem', // 44px
  12: '3rem', // 48px
  13: '3.25rem', // 52px
  14: '3.5rem', // 56px
  15: '3.75rem', // 60px
  16: '4rem', // 64px
  17: '4.25rem', // 68px
  18: '4.5rem', // 72px
  19: '4.75rem', // 76px
  20: '5rem', // 80px
  21: '5.25rem', // 84px
  22: '5.5rem', // 88px
  23: '5.75rem', // 92px
  24: '6rem', // 96px
  25: '6.25rem', // 100px
  26: '6.5rem', // 104px
  28: '7rem', // 112px
  29: '7.25rem', // 116px
  30: '7.5rem', // 120px
  32: '8rem', // 128px
  33: '8.25rem', // 132px
  34: '8.5rem', // 136px
  35: ' 8.75rem', // 140px
  36: '9rem', // 144px
  38: '9.5rem', // 150px
  40: '10rem', // 160px
  41: '10.25rem', // 164px
  42: '10.5rem', // 168px
  43: '10.75rem', // 172px
  44: '11rem', //180px
  46: '11.5rem', // 184px
  47: '11.75rem', // 188px
  48: '12rem', // 192px
  50: '12.5rem', // 200px
  52: '13rem', // 208px
  54: '13.5rem', // 216px
  56: '14rem', // 224px
  58: '14.5rem', // 232px
  60: '15rem', // 240px
  62: '15.5rem', // 248px
  64: '16rem', // 256px
  68: '17rem', // 272px
  70: '17.5rem', // 280px
  72: '18rem', // 288px
  75: '18.75rem', // 300px
  76: '19rem', // 304px
  79: '19.75rem', // 316px
  80: '20rem', // 320px
  84: '21rem', // 336px
  86: '21.5rem', // 344px
  88: '22rem', // 352px
  94: '23.5rem', // 376px
  96: '24rem', // 384px
  110: '27.5rem', // 440px
  122: '30.5rem', // 488px
  128: '32rem', // 512px
  134: '33.5rem', // 536px
  143: '35.75rem', // 572px
  148: '37rem', // 592px
  152: '38rem', // 608px
  160: '40rem', // 640px
  230: '50rem', // 800px
};

export const sizes = {
  '1/2': '50%',
  '1/3': '33.333333%',
  '2/3': '66.666667%',
  '1/4': '25%',
  '1/5': '20%',
  '2/5': '40%',
  '3/4': '75%',
  '3/5': '60%',
  '4/5': '80%',
};

export const boxShadow = {
  none: 'none',
  base: '0 0 20px 0 rgba(0, 0, 0, 0.05)',
  xl: '0 5px 30px 0 rgba(0, 0, 0, 0.1)',
  xxl: '0 20px 40px 0 rgba(0, 0, 0, 0.3)',
};

export const textShadow = {
  hunter: '0 0 20px rgba(35, 57, 51, 0.64)',
  brown: '0 0 20px rgba(81, 59, 39, 0.64)',
};

export const opacity = {
  0: 0,
  25: 0.25,
  50: 0.5,
  75: 0.75,
  95: 0.95,
  100: 1,
};

export const borderRadius = {
  base: '5px',
  sm: '7px',
  xl: '12px',
  xxl: '25px',
  xxxl: '40px',
};

export const transition = {
  base: 'ease-in 0.2s',
  fast: 'ease-in-out 0.1s',
};

export const zIndex = {
  0: 0,
  10: 10,
  15: 15,
  20: 20,
  30: 30,
  40: 40,
  50: 50,
};

export const ThemeTokens = {
  screens,
  media,
  breakpoints,
  colors,
  fonts,
  fontWeights,
  fontSizes,
  headingSizes,
  lineHeights,
  space,
  sizes,
  boxShadow,
  textShadow,
  opacity,
  borderRadius,
  transition,
  zIndex,
};
